import { ChapterSnapshot, PageSnapshot } from '@fsp/types';
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { fetchReleaseChapter } from '../services/release';
import { useAuth } from './AuthContext';

interface ChapterSnapshotContextType {
  // Chapter data
  chapter: ChapterSnapshot | null;
  pages: PageSnapshot[];
  loading: boolean;
  error: string | null;

  // Navigation helpers
  getPreviousPage: (currentPageId: string) => PageSnapshot | null;
  getNextPage: (currentPageId: string) => PageSnapshot | null;

  // Refresh
  refreshChapter: () => Promise<void>;
}

const ChapterSnapshotContext = createContext<ChapterSnapshotContextType | undefined>(undefined);

export const ChapterSnapshotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { organisationId } = useAuth();
  const { planId, releaseId, chapterId } = useParams<{
    planId: string;
    releaseId: string;
    chapterId: string;
  }>();

  const [chapter, setChapter] = useState<ChapterSnapshot | null>(null);
  const [pages, setPages] = useState<PageSnapshot[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch chapter data
  const refreshChapter = useCallback(async () => {
    if (!organisationId || !planId || !releaseId || !chapterId) {
      setChapter(null);
      setPages([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchReleaseChapter(organisationId, planId, releaseId, chapterId);
      setChapter(data.chapter);

      // Sort pages by sort order
      const sortedPages = [...data.pages].sort((a, b) => a.sortOrderAtRelease - b.sortOrderAtRelease);
      setPages(sortedPages);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch chapter');
      setChapter(null);
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [organisationId, planId, releaseId, chapterId]);

  // Auto-fetch when params change
  useEffect(() => {
    refreshChapter();
  }, [refreshChapter]);

  // Navigation helpers using useMemo for performance
  const getPreviousPage = useCallback(
    (currentPageId: string): PageSnapshot | null => {
      const currentIndex = pages.findIndex((p) => p.pageId === currentPageId);
      if (currentIndex <= 0) return null;
      return pages[currentIndex - 1];
    },
    [pages]
  );

  const getNextPage = useCallback(
    (currentPageId: string): PageSnapshot | null => {
      const currentIndex = pages.findIndex((p) => p.pageId === currentPageId);
      if (currentIndex === -1 || currentIndex >= pages.length - 1) return null;
      return pages[currentIndex + 1];
    },
    [pages]
  );

  const value = useMemo(
    () => ({
      chapter,
      pages,
      loading,
      error,
      getPreviousPage,
      getNextPage,
      refreshChapter,
    }),
    [chapter, pages, loading, error, getPreviousPage, getNextPage, refreshChapter]
  );

  return <ChapterSnapshotContext.Provider value={value}>{children}</ChapterSnapshotContext.Provider>;
};

export const useChapterSnapshot = (): ChapterSnapshotContextType => {
  const context = useContext(ChapterSnapshotContext);
  if (!context) {
    throw new Error('useChapterSnapshot must be used within a ChapterSnapshotProvider');
  }
  return context;
};
